import {
  FileValidationResult,
  InvalidFileInfo,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/types.ts';
import { fx } from '@fxts/core';
import { IMAGE_TYPES } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/constant.ts';
import { html, UnsafeHtml } from 'rune-ts';

// 이미지 타입 검증
export function validateImageType(file: File): boolean {
  return IMAGE_TYPES.includes(file.type.toLowerCase());
}

// 이미지 크기 검증 (5MB)
export function validateImageSize(file: File): boolean {
  return file.size <= 5 * 1024 * 1024;
}

// 이미지 가로/세로 크기 검증
export async function validateImageDimensions(
  file: File,
  fail_images: InvalidFileInfo[],
): Promise<FileValidationResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const min_size = 740;
        const max_size = 5000;

        if (image.width < min_size) {
          fail_images.push({ file, type: 'min_width' });
          resolve({ file, valid: false });
        } else if (image.width > max_size) {
          fail_images.push({ file, type: 'max_width' });
          resolve({ file, valid: false });
        } else if (image.height < min_size) {
          fail_images.push({ file, type: 'min_height' });
          resolve({ file, valid: false });
        } else if (image.height > max_size) {
          fail_images.push({ file, type: 'max_height' });
          resolve({ file, valid: false });
        } else {
          resolve({ file, valid: true });
        }
      };

      image.onerror = () => {
        fail_images.push({ file, type: 'load_error' });
        resolve({ file, valid: false });
      };

      image.src = reader.result as string;
    };

    reader.onerror = () => {
      fail_images.push({ file, type: 'read_error' });
      resolve({ file, valid: false });
    };

    reader.readAsDataURL(file);
  });
}

// 유효하지 않은 파일에 대한 오류 메시지 생성
export function generateInvalidFileMessage(invalidFile: InvalidFileInfo): string {
  const { file, type } = invalidFile;

  switch (type) {
    case 'invalid_type': {
      return `<strong>${file.name}</strong>\nJPG 또는 PNG 형식만 가능합니다.\n(현재 타입: ${file.type})\n`;
    }
    case 'large_size': {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

      return `<strong>${file.name}</strong>\n이미지 크기는 5MB 이하여야 합니다.\n(현재: ${fileSizeMB}MB)\n`;
    }
    case 'min_width': {
      return `<strong>${file.name}</strong>\n이미지 가로 크기는 최소 740px 이상이어야 합니다.\n`;
    }
    case 'max_width': {
      return `<strong>${file.name}</strong>\n이미지 가로 크기는 최대 5000px 이하여야 합니다.\n`;
    }
    case 'min_height': {
      return `<strong>${file.name}</strong>\n이미지 세로 크기는 최소 740px 이상이어야 합니다.\n`;
    }
    case 'max_height': {
      return `<strong>${file.name}</strong>\n이미지 세로 크기는 최대 5000px 이하여야 합니다.\n`;
    }
    case 'read_error':
    case 'load_error': {
      return `<strong>${file.name}</strong>\n이미지를 읽는 중 오류가 발생했습니다.\n`;
    }
    default: {
      return `<strong>${file.name}</strong>\n이미지 검증에 실패했습니다.\n`;
    }
  }
}

// 이미지 목록에 대한 병렬 검증 수행
export async function validateImages(files: File[], fail_images: InvalidFileInfo[]): Promise<File[]> {
  // 타입과 크기 검증
  const filesWithTypeAndSize = files.filter((file) => {
    if (!validateImageType(file)) {
      fail_images.push({ file, type: 'invalid_type' });
      return false;
    }

    if (!validateImageSize(file)) {
      fail_images.push({ file, type: 'large_size' });
      return false;
    }

    return true;
  });

  // 이미지 가로/세로 크기 검증
  const validationResults = await fx(filesWithTypeAndSize)
    .toAsync()
    .map((file) => validateImageDimensions(file, fail_images))
    .concurrent(10) // 동시에 10개의 이미지를 처리
    .toArray();

  // 유효한 파일만 필터링하여 반환
  return validationResults.filter((result) => result.valid).map((result) => result.file);
}

// 알림 메시지 생성
export function createAlertMessage(
  fail_images: InvalidFileInfo[],
  validFilesCount: number,
): { title: string; message: string | UnsafeHtml } {
  const invalidFileMessages = fail_images.map(generateInvalidFileMessage);
  let alertMessage = '선택하신 이미지 중 일부를 처리할 수 없습니다\n\n' + invalidFileMessages.join('\n');

  if (validFilesCount > 0) {
    alertMessage += '\n문제가 없는 유효한 이미지만 추가됩니다.';
  } else {
    alertMessage += '\n모든 이미지가 유효하지 않아\n추가할 수 있는 이미지가 없습니다.';
  }

  return {
    title: '선택 이미지 안내',
    message: html.preventEscape(alertMessage),
  };
}
